/**
 * OLMPS Spectrum Engine — static tool registry (the 13 DMTs).
 *
 * Pure constants, no DB, no React. Portable to React Native (mobile consumes
 * the same file). This is the design source of truth for which tool sits in
 * which gear, its Mala-D (−4 seat) candidates, and whether it is one of the
 * starter five that the MVP proves the engine on.
 *
 * See docs/OLMPS_SPECTRUM_PLAN.md and the "Spectrum Engine" design doc.
 */

import type { Gear } from '../domain/olmps';

/** Canonical tool id (lowercase) as stored in `dmt_state.tool_id`, `dmt_events.tool_id`. */
export type DmtToolId =
  | 'telos'
  | 'oracle'
  | 'astra'
  | 'numa'
  | 'moyri'
  | 'hrmez'
  | 'kyrs'
  | 'krnos'
  | 'janus'
  | 'tkne'
  | 'metis'
  | 'themis'
  | 'clio'
  | 'argos'
  | 'nemos';

/** A named Mala-D that can take the −4 seat when the tool's skill is untrained. */
export interface MaladySubtype {
  /** Stored id, e.g. 'dysnomia'. */
  id: string;
  /** Display name, e.g. 'Dysnomia'. */
  name: string;
}

/** An intake scenario question. Each option maps to a band; the fold of the
 *  answers picks the −4 subtype (for multi-candidate tools) and the start
 *  position, per the two-phase-assessment design (Phase 1, self-report). */
export interface IntakeQuestion {
  prompt: string;
  options: IntakeOption[];
}
export interface IntakeOption {
  label: string;
  /** −4..+4 contribution to the starting position. */
  score: number;
  /** Which malady subtype this answer leans toward (for multi-candidate tools). */
  leans?: string;
}

export interface DmtTool {
  id: DmtToolId;
  /** Display name, e.g. 'THEMIS'. */
  name: string;
  gear: Gear;
  /** True for the starter five (TELOS, NUMA, KYRS, THEMIS, CLIO) — the MVP set. */
  starter: boolean;
  /**
   * Competing Mala-D candidates for this tool's −4 seat. Intake picks exactly
   * one and locks it; a single-candidate tool has no real choice.
   */
  maladies: MaladySubtype[];
  /** The practical demonstration that certifies the skill (Phase 2 check). */
  certification: string;
  /** The 5–7 question intake diagnostic. */
  intake: IntakeQuestion[];
}

const m = (id: string, name: string): MaladySubtype => ({ id, name });

/**
 * The 15 Deity Mind Tools. Order follows the field-guide exploration order.
 * Mala-D candidates per the design doc's DMT × Mala-D pairings.
 */
/** A 3-band scenario option helper: malady-leaning / baseline / mastery. */
const q = (prompt: string, options: IntakeOption[]): IntakeQuestion => ({ prompt, options });

// Intake banks. Each question offers a malady-facing answer (negative score),
// a baseline (~0), and a mastery answer (positive). For multi-candidate tools
// the malady answers carry `leans` so the fold picks a subtype.
const INTAKE_TELOS: IntakeQuestion[] = [
  q('When you pick what to work on, it usually traces back to…', [
    { label: "whatever's loudest in front of me", score: -3 },
    { label: 'a rough sense of my priorities', score: 0 },
    { label: 'a declared direction I can name', score: 3 },
  ]),
  q('Looking back at a week, can you reconstruct why you did what you did?', [
    { label: 'rarely — it just happened', score: -3 },
    { label: 'roughly', score: 0 },
    { label: 'yes, each choice filtered through the north star', score: 3 },
  ]),
  q('Your projects and tasks carry a life-sector link…', [
    { label: 'almost never', score: -2 },
    { label: 'some do', score: 0 },
    { label: 'as a rule', score: 2 },
  ]),
];

const INTAKE_ORACLE: IntakeQuestion[] = [
  q('Before you act, how honestly do you actually know where you stand?', [
    { label: 'I mostly guess — I fly blind and hope it works out', score: -3 },
    { label: 'roughly, if I stop to think about it', score: 0 },
    { label: 'clearly — I take honest counsel before I move', score: 3 },
  ]),
  q('When something in your setup is off, do you notice it before it costs you?', [
    { label: 'no — it usually surprises me later', score: -3 },
    { label: 'sometimes', score: 0 },
    { label: 'yes, an intake-style check catches it early', score: 3 },
  ]),
  q('Do intake questions actually change what you work on next?', [
    { label: 'never — I skip straight to doing', score: -2 },
    { label: 'occasionally', score: 0 },
    { label: 'yes, they prescribe the work', score: 2 },
  ]),
];

const INTAKE_ASTRA: IntakeQuestion[] = [
  q('Across Social, Health, Admin, Personal, Economic, how many are you actually watching?', [
    { label: 'one or two — the rest drift unseen', score: -3 },
    { label: 'most of them, loosely', score: 0 },
    { label: 'all five, in one view, every week', score: 3 },
  ]),
  q('When one sector eats all your time, do you notice the others slipping?', [
    { label: 'no — I only notice once it’s a crisis', score: -3 },
    { label: 'eventually', score: 0 },
    { label: 'immediately — the whole compass is in view', score: 3 },
  ]),
  q('Do you steer effort by the whole compass, or by whatever sector is loudest?', [
    { label: 'whatever’s loudest wins', score: -2 },
    { label: 'a mix', score: 0 },
    { label: 'I steer by the whole compass', score: 2 },
  ]),
];

const INTAKE_NUMA: IntakeQuestion[] = [
  q('On a demanding day your energy tends to…', [
    { label: 'run a constant hum of overwhelm', score: -3, leans: 'oizys' },
    { label: 'quietly erode week over week', score: -3, leans: 'phthisis' },
    { label: 'stall — I know what to do but cannot start', score: -3, leans: 'aergia' },
    { label: 'hold steady enough', score: 0 },
    { label: 'get matched to the work deliberately', score: 3 },
  ]),
  q('When the tank is low you…', [
    { label: 'push through and crash', score: -2, leans: 'oizys' },
    { label: 'fade and produce less each day', score: -2, leans: 'phthisis' },
    { label: 'go inactive', score: -2, leans: 'aergia' },
    { label: 'sometimes notice, sometimes not', score: 0 },
    { label: 'trim the plan to capacity', score: 3 },
  ]),
  q('Do you read your capacity before committing work?', [
    { label: 'never', score: -2 },
    { label: 'occasionally', score: 0 },
    { label: 'every block', score: 2 },
  ]),
];

const INTAKE_KYRS: IntakeQuestion[] = [
  q('When a lot competes for attention you…', [
    { label: 'miss what actually mattered, in hindsight', score: -3, leans: 'koalemos' },
    { label: 'make progress then undo it before it counts', score: -3, leans: 'oknos' },
    { label: 'muddle through', score: 0 },
    { label: 'quickly tell what matters most and act on it', score: 3 },
  ]),
  q('Loud, low-priority items vs. your real priorities…', [
    { label: 'the loud ones usually win', score: -3, leans: 'koalemos' },
    { label: 'it varies', score: 0 },
    { label: 'I work the list top-down', score: 3 },
  ]),
  q('Do your tasks get scored/ranked at all?', [
    { label: 'no', score: -2 },
    { label: 'informally', score: 0 },
    { label: 'yes, and I trust the ranking', score: 2 },
  ]),
];

const INTAKE_THEMIS: IntakeQuestion[] = [
  q('A routine you run repeatedly comes out…', [
    { label: 'different every time — no rule holds', score: -3 },
    { label: 'roughly the same', score: 0 },
    { label: 'identically, on rails', score: 3 },
  ]),
  q('Mid-routine, when something interrupts you…', [
    { label: 'I skip steps or abandon it', score: -3 },
    { label: 'I usually get back to it', score: 0 },
    { label: 'I resume and finish the sequence', score: 3 },
  ]),
  q('Do you follow a saved protocol or improvise each time?', [
    { label: 'improvise from memory', score: -2 },
    { label: 'a bit of both', score: 0 },
    { label: 'run the saved protocol', score: 2 },
  ]),
];

const INTAKE_CLIO: IntakeQuestion[] = [
  q('After a rough stretch, the lesson…', [
    { label: 'is forgotten; I repeat the mistake', score: -3 },
    { label: 'sometimes sticks', score: 0 },
    { label: 'gets pulled and changes what I do', score: 3 },
  ]),
  q('Your morning + night sessions…', [
    { label: 'rarely happen', score: -3 },
    { label: 'happen some days', score: 0 },
    { label: 'bookend the day reliably', score: 3 },
  ]),
  q('Do reflections turn into actual changes elsewhere?', [
    { label: 'no, just venting', score: -2 },
    { label: 'occasionally', score: 0 },
    { label: 'yes — a habit or protocol edit follows', score: 2 },
  ]),
];

const GENERIC_INTAKE: IntakeQuestion[] = [
  q('Right now, this skill is…', [
    { label: 'clearly working against me', score: -3 },
    { label: 'neutral — untrained but not costing me', score: 0 },
    { label: 'something I do well', score: 3 },
  ]),
];

export const DMT_TOOLS: readonly DmtTool[] = [
  {
    id: 'telos', name: 'TELOS', gear: 'orient', starter: true,
    maladies: [m('plane', 'Planē')],
    certification: 'Run a full week where every major decision is filtered through the north star and logged as such.',
    intake: INTAKE_TELOS,
  },
  {
    id: 'oracle', name: 'ORACLE', gear: 'orient', starter: false,
    maladies: [m('amathia', 'Amathia')],
    certification: 'Run a full week of intake reviews where the answers actually prescribe what you work on, not just describe it.',
    intake: INTAKE_ORACLE,
  },
  {
    id: 'astra', name: 'ASTRA', gear: 'orient', starter: false,
    maladies: [m('dysastria', 'Dysastria')],
    certification: 'Hold all five life sectors in one compass view for a full month with no sector drifting unseen.',
    intake: INTAKE_ASTRA,
  },
  {
    id: 'numa', name: 'NUMA', gear: 'regulate', starter: true,
    maladies: [m('oizys', 'Oizys'), m('phthisis', 'Phthisis'), m('aergia', 'Aergia')],
    certification: "Forecast a week's capacity and land within tolerance, adjusting deliberately as reality shifts.",
    intake: INTAKE_NUMA,
  },
  {
    id: 'moyri', name: 'MoyRi', gear: 'regulate', starter: false,
    maladies: [m('akrasia', 'Akrasia'), m('kakia', 'Kakia'), m('adrasteia', 'Adrasteia')],
    certification: 'Recover a deliberately broken habit within the window without abandoning it.',
    intake: GENERIC_INTAKE,
  },
  {
    id: 'hrmez', name: 'HRMEZ', gear: 'regulate', starter: false,
    maladies: [m('homados', 'Homados'), m('oistros', 'Oistros')],
    certification: 'Hold the inbox at near-zero age for a full week through a heavy inflow period.',
    intake: GENERIC_INTAKE,
  },
  {
    id: 'kyrs', name: 'KYRS', gear: 'direct', starter: true,
    maladies: [m('koalemos', 'Koalemos'), m('oknos', 'Oknos')],
    certification: 'Work top-down the scored list for a week, including two high-pressure days, without the list breaking.',
    intake: INTAKE_KYRS,
  },
  {
    id: 'krnos', name: 'KRNOS', gear: 'direct', starter: false,
    maladies: [m('chaos', 'Chaos')],
    certification: 'Build and follow a realistic week, including at least one deliberate reschedule when reality changes.',
    intake: GENERIC_INTAKE,
  },
  {
    id: 'janus', name: 'JANUS', gear: 'execute', starter: false,
    maladies: [m('aporia', 'Aporia')],
    certification: 'Resolve a meaningful decision through the tool and keep it closed for the full window.',
    intake: GENERIC_INTAKE,
  },
  {
    id: 'tkne', name: 'TKNE', gear: 'direct', starter: false,
    maladies: [m('amechania', 'Amechania'), m('atlas', 'Atlas')],
    certification: 'Structure a real project from outcome through milestones to a concrete next action.',
    intake: GENERIC_INTAKE,
  },
  {
    id: 'metis', name: 'METIS', gear: 'execute', starter: false,
    maladies: [m('ate', 'Atē')],
    certification: 'Follow the live agenda through a deliberately disrupted day, resuming the plan each time.',
    intake: GENERIC_INTAKE,
  },
  {
    id: 'themis', name: 'THEMIS', gear: 'execute', starter: true,
    maladies: [m('dysnomia', 'Dysnomia')],
    certification: 'Complete a full protocol under disruption without silently skipping a single step.',
    intake: INTAKE_THEMIS,
  },
  {
    id: 'clio', name: 'CLIO', gear: 'revise', starter: true,
    maladies: [m('lethe', 'Lethe')],
    certification: 'Run a full week of morning + night sessions where at least one lesson becomes a system change.',
    intake: INTAKE_CLIO,
  },
  {
    id: 'argos', name: 'ARGOS', gear: 'revise', starter: false,
    maladies: [m('achlys', 'Achlys'), m('apate', 'Apate')],
    certification: "Review a week's actual vs. planned attention and visibly adjust the next week from it.",
    intake: GENERIC_INTAKE,
  },
  {
    id: 'nemos', name: 'NEMOS', gear: 'revise', starter: false,
    maladies: [m('dysnomia', 'Dysnomia')],
    certification: 'Hold a week with zero misfiled or duplicated items and consistently fast retrieval.',
    intake: GENERIC_INTAKE,
  },
] as const;

/**
 * Fold intake answers (an array of chosen options) into a starting position
 * (−4..+4) and the leaning malady subtype. Averages the option scores; picks
 * the most-voted `leans` (fallback: the tool's first candidate).
 */
export function foldIntake(
  tool: DmtTool,
  chosen: IntakeOption[],
): { startPosition: number; subtype: string } {
  const avg = chosen.length ? chosen.reduce((a, o) => a + o.score, 0) / chosen.length : 0;
  const startPosition = Math.max(-4, Math.min(4, Math.round(avg)));

  const votes = new Map<string, number>();
  for (const o of chosen) if (o.leans) votes.set(o.leans, (votes.get(o.leans) ?? 0) + 1);
  let subtype = tool.maladies[0]?.id ?? '';
  let best = 0;
  for (const [id, n] of votes) if (n > best) ((best = n), (subtype = id));
  return { startPosition, subtype };
}

const BY_ID = new Map<DmtToolId, DmtTool>(DMT_TOOLS.map((t) => [t.id, t]));

export function getTool(id: DmtToolId): DmtTool {
  const t = BY_ID.get(id);
  if (!t) throw new Error(`Unknown DMT tool id: ${id}`);
  return t;
}

/** The starter five, in gear order — the MVP set the engine is proven on. */
export const STARTER_TOOLS: readonly DmtTool[] = DMT_TOOLS.filter((t) => t.starter);
